import { Controller } from "@hotwired/stimulus";

/**
 * Shader Button Controller
 *
 * Creates an animated mesh gradient background for buttons using the Paper Design shader library.
 * The shader canvas is placed behind the button content, creating a "living" background effect.
 * Supports dark mode with automatic color switching.
 */
export default class extends Controller<HTMLElement> {
  private shaderMount: any = null;
  private themeObserver: MutationObserver | null = null;
  private mediaQuery: MediaQueryList | null = null;
  private systemThemeListener: ((e: MediaQueryListEvent) => void) | null = null;
  private getShaderColorFromString: ((color: string) => number[]) | null = null;

  // Speed animation
  private baseSpeed = 0.6;
  private hoverSpeed = 2.5;
  private currentSpeed = 0.6;
  private targetSpeed = 0.6;
  private speedAnimationFrame: number | null = null;

  // Light mode colors - primary green with grey/silver (matching hero mesh gradient)
  private lightColors = [
    "rgba(191, 237, 126, 1)",   // Primary green (#BFED7E)
    "rgba(202, 199, 191, 1)",   // Warm grey/silver (#cac7bf)
    "rgba(196, 240, 163, 1)",   // Light green (#c4f0a3)
    "rgba(226, 224, 218, 1)",   // Light grey/silver (#e2e0da)
  ];

  // Dark mode colors - darker base with green accent
  private darkColors = [
    "rgba(45, 43, 39, 1)",      // Darker grey (#2d2b27)
    "rgba(61, 58, 53, 1)",      // Dark grey (#3d3a35)
    "rgba(140, 200, 100, 1)",   // Muted green accent
    "rgba(50, 48, 44, 1)",      // Dark grey (#32302c)
  ];

  async connect(): Promise<void> {
    await this.initShader();
    this.setupThemeObserver();
    this.setupSystemThemeListener();
    this.setupHoverListeners();
  }

  disconnect(): void {
    if (this.shaderMount?.dispose) {
      this.shaderMount.dispose();
    }
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    if (this.speedAnimationFrame) {
      cancelAnimationFrame(this.speedAnimationFrame);
    }
    this.removeSystemThemeListener();
  }

  private setupHoverListeners(): void {
    this.element.addEventListener("mouseenter", this.onMouseEnter.bind(this));
    this.element.addEventListener("mouseleave", this.onMouseLeave.bind(this));
  }

  private onMouseEnter(): void {
    this.targetSpeed = this.hoverSpeed;
    this.animateSpeed();
  }

  private onMouseLeave(): void {
    this.targetSpeed = this.baseSpeed;
    this.animateSpeed();
  }

  private animateSpeed(): void {
    if (this.speedAnimationFrame) {
      cancelAnimationFrame(this.speedAnimationFrame);
    }

    const easeSpeed = () => {
      const diff = this.targetSpeed - this.currentSpeed;
      const easing = 0.08; // Adjust for faster/slower easing

      if (Math.abs(diff) < 0.01) {
        this.currentSpeed = this.targetSpeed;
        this.shaderMount?.setSpeed?.(this.currentSpeed);
        return;
      }

      this.currentSpeed += diff * easing;
      this.shaderMount?.setSpeed?.(this.currentSpeed);
      this.speedAnimationFrame = requestAnimationFrame(easeSpeed);
    };

    easeSpeed();
  }

  private isDarkMode(): boolean {
    const dataTheme = document.documentElement.getAttribute("data-theme");
    if (dataTheme) {
      return dataTheme === "lumenfall-dark";
    }
    const savedTheme = localStorage.getItem("lumenTheme");
    if (savedTheme === "dark") return true;
    if (savedTheme === "light") return false;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  }

  private getColors(): string[] {
    return this.isDarkMode() ? this.darkColors : this.lightColors;
  }

  private setupThemeObserver(): void {
    this.themeObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "data-theme") {
          this.updateColors();
        }
      }
    });

    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
  }

  private setupSystemThemeListener(): void {
    if (!window.matchMedia) return;

    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    this.systemThemeListener = () => {
      const savedTheme = localStorage.getItem("lumenTheme");
      if (!savedTheme || savedTheme === "system") {
        this.updateColors();
      }
    };

    this.mediaQuery.addEventListener("change", this.systemThemeListener);
  }

  private removeSystemThemeListener(): void {
    if (this.mediaQuery && this.systemThemeListener) {
      this.mediaQuery.removeEventListener("change", this.systemThemeListener);
    }
  }

  private updateColors(): void {
    if (!this.shaderMount || !this.getShaderColorFromString) return;

    const colorStrings = this.getColors();
    const colors = colorStrings.map((c) => this.getShaderColorFromString!(c));

    this.shaderMount.setUniforms({
      u_colors: colors,
    });
  }

  private async initShader(): Promise<void> {
    try {
      const {
        ShaderMount,
        meshGradientFragmentShader,
        getShaderColorFromString,
      } = await import("@paper-design/shaders");

      this.getShaderColorFromString = getShaderColorFromString;

      const colorStrings = this.getColors();
      const colors = colorStrings.map((c) => getShaderColorFromString(c));

      this.shaderMount = new ShaderMount(
        this.element,
        meshGradientFragmentShader,
        {
          u_colors: colors,
          u_colorsCount: colors.length,
          u_distortion: 0.25,
          u_swirl: 0.5,
          u_grainMixer: 0,
          u_grainOverlay: 0.05,
          u_scale: 1.2,
          u_rotation: 30,
        },
        { alpha: true, premultipliedAlpha: true },
        0.6 // Animation speed
      );
    } catch (error) {
      console.error("Failed to initialize shader button:", error);
    }
  }
}
